import { AppType } from "client/Api/Apps/common";
import {
	BookmarksProvider,
	DocumentsLiveInformationProvider
} from "client_legacy/Workspace/LiveInformation/Documents/provider";
import { ReactElement, useCallback } from "react";
import { useRenderManager } from "../../useRenderManager";
import { OneByOneTileContent } from "../OneByOneTileContent";
import { TwoByTwoTileContent } from "../TwoByTwoTileContent";
import { DocumentsLiveTileContent } from "./DocumentsLiveTileContent";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { navigateToDocumentsApp } from "client_legacy/Apps/appNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type DocumentsTileContentProps = {
	renderState: TileRenderState<AppType.Documents>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
	documentsLiveInformationProvider: DocumentsLiveInformationProvider;
	bookmarksProvider: BookmarksProvider;
};

export const DocumentsTileContent = (props: DocumentsTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu,
		documentsLiveInformationProvider,
		bookmarksProvider
	} = props;

	const [navigationElement, navigateHandler] = useDocumentsTileNavigation(
		renderState,
		onEdit
	);

	const {
		dimension,
		data: { settings }
	} = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<DocumentsLiveTileContent
				tabs={settings.tabs}
				destinationWindow={settings.destinationWindow}
				documentsLiveInformationProvider={documentsLiveInformationProvider}
				bookmarksProvider={bookmarksProvider}
				onNavigate={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		],
		[
			{ columnSpan: 2, rowSpan: 2 },
			() => (
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
DocumentsTileContent.displayName = "DocumentsTileContent";

export function useDocumentsTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Documents>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	const { settings, environmentAppId } = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				registerTileUsage();

				navigateToDocumentsApp({
					id: environmentAppId,
					destinationWindow: settings.destinationWindow
				});
			},
			[
				environmentAppId,
				registerTileUsage,
				settings.destinationWindow
			]
		)
	);
}
